/* 被動與專屬效果的時間點。
 * 這裡只決定「什麼時候」呼叫各武將登記的處理（core/hooks.ts），「做什麼」寫在各武將自己的檔案（heroes/<陣營>/<id>.ts）。
 * 另外放幾個多名武將共用的機制：被動標記 u.sg、自訂投射物、被看穿的敵人、暫時的城牆、電腦用招的判斷。 */
// @ts-nocheck — 型別在第 4 步補
import { SG } from './sg.ts';
import { at, chain } from './hooks.ts';

const W = SG.World.prototype, L = SG.L, K = SG.K;
const gd = SG.gd, along = SG.along;
const sigOf = (u, name) => (u.sg ? u.sg[name] : null); // { sk, lv, v(key) }

// 各時間點的登記表（武將檔比這裡早載入，登記已經完成）
const H_CAST_AFTER = at('cast.after');
const H_CAST_BEFORE = at('cast.before');
const H_CAST_OK = at('cast.ok');
const H_CAST_PRE = at('cast.pre');
const H_CC_AFTER = at('cc.after');
const H_CC_BLOCK = at('cc.block');
const H_CC_TENACITY = at('cc.tenacity');
const H_CONTROL_PRE = at('control.pre');
const H_DAMAGE_AFTER = at('damage.after');
const H_DAMAGE_BEFORE = at('damage.before');
const H_GOTOWARD_PRE = at('goToward.pre');
const H_HEAL_MUL = at('heal.mul');
const H_ISENEMY_NO = at('isEnemy.no');
const H_KILL_AFTER = at('kill.after');
const H_ONHIT = at('onHit');
const H_PREKILL = at('preKill');
const H_RECALC = at('recalc');
const H_REVIVE = at('revive');
const H_STEP_AFTER = at('step.after');
const H_STEP_END = at('step.end');
const H_STEP_HERO = at('step.hero');
const H_TICK = at('tick');
const H_VISIBILITY_HERO = at('visibility.hero');
const H_ZONE = at('zone');

// ---------- 武將檔裡的專屬技能（x.k）與專屬方法，掛到 World 上 ----------
for (const m of SG.HERO_MODS || []) {
  for (const k in m.kinds) W['x_' + k] = m.kinds[k];
  for (const k in m.methods) W[k] = m.methods[k];
}

// ---------- 形態（v0.5.0）：R 用 x.k:'form' 切到下一個形態 ----------
// 每個形態一份 spec（heroes/define.ts buildForms）；冷卻各一組，R 的冷卻共用；遠近程跟著形態，再重算射程與數值
const H_FORM = at('form');
W.setForm = function (u, f) {
  const forms = u.spec.forms;
  if (!forms || !forms[f] || u.form === f) return;
  const from = u.form || 0;
  if (!u.cds) u.cds = forms.map(() => [0, 0, 0, 0]);
  u.cds[from] = u.cd; // u.cd 可能被整個換掉（例如測試、連線）：以目前這組為準
  const next = u.cds[f];
  next[3] = u.cd[3];
  // 造型（換色）是套在開場那份設定上的：每個形態都套同一個造型，切回來也還在
  if (!u.formSpecs) {
    const s0 = u.spec;
    u.formSpecs = forms.map((fm, k) => (k === from ? s0 : s0.skinId ? Object.assign(Object.create(fm.spec), { c: s0.c, fx: s0.fx, aura: s0.aura, acc: s0.acc, legend: s0.legend, skinId: s0.skinId, skinName: s0.skinName }) : fm.spec));
  }
  u.form = f; u.spec = u.formSpecs[f]; u.cd = next; u.ranged = !!forms[f].ranged; u.formT = this.t;
  if (u.act && u.act.kind === 'attack') u.act = null; // 換兵器：取消出手中的普攻
  u.tgOn = null; u.rc = null;
  this.recalc(u);
  for (const h of H_FORM) h.fn.call(this, u, from, f);
};
W.x_form = function (u) {
  const n = u.spec.forms ? u.spec.forms.length : 0;
  if (n > 1) this.setForm(u, ((u.form || 0) + 1) % n);
};

// ---------- 被動標記：學會、升級時重算 ----------
const xRecalc0 = W.xRecalc;
W.xRecalc = function (u) {
  xRecalc0.call(this, u);
  if (u.kind !== 'hero' || !u.spec.skills) return;
  let sg = null;
  u.spec.skills.forEach((sk, i) => {
    if (!sk.sig || !u.skillLv[i]) return;
    const lv = u.skillLv[i];
    (sg || (sg = {}))[sk.sig] = { sk, lv, i, v: (k) => L(sk[k], lv) };
  });
  u.sg = sg;
  for (const h of H_RECALC) h.fn.call(this, u, sg);
  if (u.rangeB) for (const k in u.rangeB) u.range += u.rangeB[k]; // 暫時的射程加成（見 setRangeBonus）
};
// 暫時的射程加成：依來源記錄（郝昭「陳倉城壘」、曹純「回馬箭」），重算數值後會自動加回
W.setRangeBonus = function (u, key, v) {
  const b = u.rangeB || (u.rangeB = {}), old = b[key] || 0;
  if (old === v) return;
  b[key] = v; u.range += v - old;
};

// ---------- 自訂投射物：不經過 sim 的命中流程，命中與結束都交給回呼 ----------
// p.custom 為 true；p.filter(t) 決定誰擋得住；p.onHit(t) 回傳 true 表示停下；p.onEnd() 飛完射程時呼叫
const updateProjs0 = W.updateProjs;
W.updateProjs = function (dt) {
  const mine = [];
  this.projs = this.projs.filter((p) => (p.custom ? (mine.push(p), false) : true));
  updateProjs0.call(this, dt);
  for (const p of mine) if (this.stepCustomProj(p, dt)) this.projs.push(p);
  this._el = null; this._src = null;
};
W.stepCustomProj = function (p, dt) {
  p.t += dt; this._el = p.el || null; this._src = p.owner ? p.owner.id : null;
  if (p.homing) {
    const t = p.homing;
    if (!t.alive) return false;
    const dx = t.x - p.x, dy = t.y - p.y, d = Math.hypot(dx, dy), s = p.speed * dt;
    p.f = Math.sign(dx) || p.f; p.ang = Math.atan2(dy, dx); p.vx = (dx / (d || 1)) * p.speed; p.vy = (dy / (d || 1)) * p.speed;
    if (d <= s + 10) { p.onHit(t); return false; }
    p.x += (dx / d) * s; p.y += (dy / d) * s;
    return true;
  }
  p.x += p.vx * dt; p.y += p.vy * dt; p.dist += p.speed * dt;
  if (p.onHit) for (const t of this.units) {
    if (!this.hostile(p.owner, t) || p.hit[t.id] || (p.filter && !p.filter(t))) continue;
    if (gd(p.x, p.y, t.x, t.y) > t.r + p.rad) continue;
    p.hit[t.id] = 1;
    if (p.onHit(t)) return false;
  }
  if (p.dist >= p.range) { if (p.onEnd) p.onEnd(); return false; }
  return true;
};
W.shootProj = function (u, o) {
  const dir = o.dir || [u.f, 0], start = along(u.x, u.y, dir, 30);
  const p = Object.assign({ custom: true, team: u.team, owner: u, x: start[0], y: start[1], vx: dir[0] * o.speed, vy: dir[1] * o.speed * K, f: Math.sign(dir[0]) || u.f, dist: 0, rad: 14, hit: {}, t: 0, visual: true, el: this._el || null }, o);
  if (o.homing) { p.x = u.x; p.y = u.y - 40; }
  this.projs.push(p);
  return p;
};

// ---------- 傷害：減傷、易傷、記錄 ----------
// damage.before：依順位調整傷害（c.a），回傳 true 就擋下；damage.after：實際扣血之後
const damage0 = W.damage;
W.damage = function (src, t, amount, o) {
  if (!t.alive) return damage0.call(this, src, t, amount, o);
  // sk：這一下來自技能（在掛勾之前就要標好，被動「被技能打中」才認得舊式招式；sim.ts 的 damage 也會補標）
  if (!(o && (o.sk || o.basic)) && ((o && o.spell) || this._xa)) o = Object.assign({}, o, { sk: true });
  const own = src.owner || src;
  const c = { src, own, t, a: amount, o, act: own.act && own.act.kind === 'skill' ? own.act : null, base: damage0 };
  for (const h of H_DAMAGE_BEFORE) if (h.fn.call(this, c)) return;
  const h0 = t.hp;
  damage0.call(this, src, t, c.a, o);
  const lost = Math.max(0, h0 - Math.max(0, t.hp));
  for (const h of H_DAMAGE_AFTER) h.fn.call(this, c, lost);
};
// 即將陣亡：回傳 true 表示撐住不死
const xPreKill0 = W.xPreKill;
W.xPreKill = function (src, t) {
  for (const h of H_PREKILL) if (h.fn.call(this, src, t)) return true;
  return xPreKill0.call(this, src, t);
};

// ---------- 控制：擋下、縮短硬控制時間 ----------
const applyCC0 = W.applyCC;
W.applyCC = function (src, t, cc) {
  const own = src && (src.owner || src);
  const c = { src, own, t, cc, base: applyCC0 };
  for (const h of H_CC_BLOCK) if (h.fn.call(this, c)) return;
  let ten = 0;
  if (cc && t.alive && t.kind === 'hero' && SG.HARD_CC.some((k) => cc[k])) for (const h of H_CC_TENACITY) ten += h.fn.call(this, t, cc) || 0;
  if (!ten) return applyCC0.call(this, src, t, cc);
  const m = Math.max(0.2, 1 - ten / 100), c2 = Object.assign({}, cc);
  for (const k of SG.HARD_CC) if (c2[k]) c2[k] *= m;
  applyCC0.call(this, src, t, c2);
  for (const h of H_CC_AFTER) h.fn.call(this, t, cc);
};

// ---------- 普攻、治療、投射物：中介層，各武將依順位處理，要繼續就呼叫 next() ----------
W.attackHit = chain('attack', W.attackHit);
W.proj = chain('proj', W.proj);
// 治療常常被呼叫：heal.mul(t, v) 回傳調整後的量
const heal0 = W.heal, HEAL = at('heal.mul');
W.heal = function (t, v) { for (let i = 0; i < HEAL.length; i++) v = HEAL[i].fn.call(this, t, v); return heal0.call(this, t, v); };
const onHitProcs0 = W.onHitProcs;
W.onHitProcs = function (u, t) {
  onHitProcs0.call(this, u, t);
  for (const h of H_ONHIT) h.fn.call(this, u, t);
};

// ---------- 放技能 ----------
// cast.before 可以整個接手（回傳 true）；cast.pre 在一般流程前；cast.ok 一般流程剛成功；cast.after 依順位處理施放後的效果
const tryCast0 = W.tryCast;
W.tryCast = function (u, i, x, y) {
  for (const h of H_CAST_BEFORE) if (h.fn.call(this, u, i, x, y)) return true;
  const sk = u.spec.skills && SG.skillAt(u, i);
  for (const h of H_CAST_PRE) h.fn.call(this, u, i, x, y);
  const ok = tryCast0.call(this, u, i, x, y);
  if (ok) for (const h of H_CAST_OK) h.fn.call(this, u, i);
  if (!ok) return ok;
  for (const h of H_CAST_AFTER) h.fn.call(this, u, i, sk);
  return ok;
};

// ---------- 視野：被看穿的敵人（劉曄「料事」、楊修「揣度上意」、李典「博望識伏」），以及各武將的特殊視野 ----------
const visibility0 = W.visibility;
W.visibility = function () {
  visibility0.call(this);
  for (const u of this.units) if (u.exposed && u.alive) for (let T = 0; T < 2; T++) if (u.exposed[T] > this.t) u.vis[T] = true;
  for (const h of this.units) {
    if (h.kind !== 'hero' || !h.alive || !h.sg) continue;
    for (const k of H_VISIBILITY_HERO) k.fn.call(this, h);
  }
};
W.expose = function (t, team, sec) { (t.exposed || (t.exposed = [0, 0]))[team] = Math.max(t.exposed[team], this.t + sec); t.revealT = Math.max(t.revealT || 0, this.t + sec); };

// ---------- 區域：每個區域每步讓各武將處理，回傳 true 表示處理完（不走一般流程） ----------
const segDist = (x, y, s) => { // 點到線段的地面距離
  const ax = s[0], ay = s[1] / K, bx = s[2], by = s[3] / K, px = x, py = y / K;
  const vx = bx - ax, vy = by - ay, l2 = vx * vx + vy * vy || 1, q = Math.max(0, Math.min(1, ((px - ax) * vx + (py - ay) * vy) / l2));
  return Math.hypot(px - ax - vx * q, py - ay - vy * q);
};
SG.segDist = segDist;
const xZone0 = W.xZone;
W.xZone = function (z, dt) {
  for (const h of H_ZONE) if (h.fn.call(this, z, dt)) return true;
  return xZone0.call(this, z, dt);
};

// ---------- 每個步驟：每名活著的武將（step.hero → tick），全部處理完（step.after），最後（step.end） ----------
const step0 = W.step;
W.step = function (dt) {
  SG._liveWorld = this; // 暫時的城牆只對目前在跑的世界有效
  step0.call(this, dt);
  if (this.over === null) {
    for (const u of this.units) {
      if (u.kind !== 'hero' || !u.alive) continue;
      for (const h of H_STEP_HERO) h.fn.call(this, u, dt);
      this.heroTick(u, dt);
    }
    for (const h of H_STEP_AFTER) h.fn.call(this, dt);
  }
  if (this.over !== null) return;
  for (const h of H_STEP_END) h.fn.call(this, dt);
};
W.heroTick = function (u, dt) { for (const h of H_TICK) h.fn.call(this, u, dt); };

// ---------- 陣亡、復活 ----------
const kill0 = W.kill;
W.kill = function (src, t) {
  const was = t.alive && t.kind === 'hero';
  kill0.call(this, src, t);
  for (const h of H_KILL_AFTER) h.fn.call(this, src, t, was);
};
// 復活：各武將清掉自己進行中的特殊狀態
const revive0 = W.revive;
W.revive = function (u) {
  for (const h of H_REVIVE) h.fn.call(this, u);
  u.invuln = false; u.untargetable = false;
  return revive0.call(this, u);
};

// ---------- 敵我判斷、每步操作、走向某點：中介層 ----------
// 這三個每步、每個單位都會呼叫很多次，所以不用中介層：處理函式回傳 true 表示攔下
//   isEnemy.no(u, t)：回傳 true 表示「不當成敵人」；control.pre(u, dt)：操作前調整指令；goToward.pre(u, x, y, dt)：回傳 true 表示已經處理移動
const isEnemy0 = W.isEnemy, NOT_ENEMY = at('isEnemy.no');
W.isEnemy = function (u, t) { for (let i = 0; i < NOT_ENEMY.length; i++) if (NOT_ENEMY[i].fn.call(this, u, t)) return false; return isEnemy0.call(this, u, t); };
const control0 = W.control, CONTROL = at('control.pre');
W.control = function (u, dt) { for (let i = 0; i < CONTROL.length; i++) CONTROL[i].fn.call(this, u, dt); return control0.call(this, u, dt); };
const goToward0 = W.goToward, GOTO = at('goToward.pre');
W.goToward = function (u, x, y, dt) { for (let i = 0; i < GOTO.length; i++) if (GOTO[i].fn.call(this, u, x, y, dt)) return; return goToward0.call(this, u, x, y, dt); };

// ---------- 暫時的城牆（滿寵「合肥新城」、李典「舉宗遷鄴」）----------
// 牆存在 SG.MAP 上但只對目前在跑的世界有效；M.walkable 包一層，移動、落點、投射物都會被擋
// 換地圖（SG.useMap）後要重新包一次
const M = SG.MAP;
const wrapWalls = () => {
  const walk0 = M.walkable;
  SG.walkBase = walk0; // 不含暫時城牆的可走判斷（李典的兵牆推人時用）
  M.walls = [];
  M.walkable = function (x, y, pad) {
    if (!walk0(x, y, pad)) return false;
    const ws = M.walls;
    if (!ws.length) return true;
    for (const wl of ws) if (wl.kind !== 'fake' && wl.w === SG._liveWorld && wl.until > wl.w.t && segDist(x, y, wl.seg) <= wl.thick / 2 + (pad || 0)) return false; // fake：徐盛「疑城」只是畫出來的假城牆
    return true;
  };
};
wrapWalls();
SG.onMapSwap(wrapWalls);
W.addWall = function (seg, thick, dur, team, kind) {
  M.walls = M.walls.filter((wl) => wl.w === this && wl.until > this.t);
  const wl = { w: this, seg, thick, until: this.t + dur, team, kind, t0: this.t };
  // 被牆夾到的人推到最近的一側
  const ax = seg[0], ay = seg[1] / K, bx = seg[2], by = seg[3] / K, L0 = Math.hypot(bx - ax, by - ay) || 1, nx = -(by - ay) / L0, ny = (bx - ax) / L0;
  for (const u of this.units) {
    if (kind === 'fake' || !u.alive || u.structure || u.kind === 'ward' || segDist(u.x, u.y, seg) > thick / 2 + 26) continue;
    const side = ((u.x - ax) * nx + (u.y / K - ay) * ny) >= 0 ? 1 : -1, need = thick / 2 + 30 - side * ((u.x - ax) * nx + (u.y / K - ay) * ny);
    u.x += nx * side * need; u.y += ny * side * need * K;
  }
  M.walls.push(wl);
  return wl;
};

// ---------- 電腦什麼時候用這些招：規則寫在各武將檔的 ai（依技能種類）與 aiSig（依被動標記） ----------
const AI = {}, AI_SIG = {};
for (const m of SG.HERO_MODS || []) { Object.assign(AI, m.ai); Object.assign(AI_SIG, m.aiSig); }
const GENERIC = ['nuke', 'multi', 'aoe', 'buff', 'zone', 'summon', 'line', 'blinkhit', 'pullally', 'fullheal', 'healwave', 'vision', 'dispel', 'meteor', 'blink', 'feast', 'transform', 'flurry', 'detonate'];
SG.aiUseful = function (w, u, i, t, sk) {
  const k = sk.x && sk.x.k;
  if (k === 'form') return formWanted(w, u, t) != null;
  if (sk.sig && AI_SIG[sk.sig]) return AI_SIG[sk.sig]({ w, u, t });
  if (!k || !W['x_' + k] || GENERIC.includes(k)) return undefined;
  const d = t ? gd(u.x, u.y, t.x, t.y) : 1e9, hero = !!t && t.kind === 'hero', hpR = u.hp / u.maxHp;
  const foeHeroes = (r) => w.enemiesNear(u, u.x, u.y, r).filter((e) => e.kind === 'hero').length;
  return AI[k] ? AI[k]({ w, u, t, d, hero, hpR, foeHeroes }) : undefined;
};

// 電腦要不要切換形態：武將檔的 formAi 回傳要切到的形態；沒寫就用預設規則
// （目標在近戰射程＋150 外用遠程形態、以內用近戰形態；切換後 2 秒內不再切）
function formWanted(w, u, t) {
  const forms = u.spec.forms, cur = u.form || 0;
  if (!forms || (u.formT || -99) > w.t - 2) return null;
  const d = t ? gd(u.x, u.y, t.x, t.y) : 1e9, hero = !!t && t.kind === 'hero', hpR = u.hp / u.maxHp;
  const foeHeroes = (r) => w.enemiesNear(u, u.x, u.y, r).filter((e) => e.kind === 'hero').length;
  let f;
  if (u.spec.formAi) f = u.spec.formAi({ w, u, t, d, hero, hpR, foeHeroes, form: cur });
  else { if (!t) return null; const want = d > 240 ? forms.findIndex((x) => x.ranged) : forms.findIndex((x) => !x.ranged); f = want < 0 ? null : want; }
  return f == null || f === cur ? null : f;
}
